import Papa from "papaparse";

import { downloadCsvWithBom } from "~/src/lib/utils";

import {
  FunnelImportRowValues,
  ImportPageValues,
  defaultPageValues,
} from "./validationSchema";

export const IMPORT_MAX_FUNNELS = 200;

export const FUNNEL_IMPORT_COLUMN_COUNT_WITH_ACTION = 33;
export const FUNNEL_IMPORT_COLUMN_COUNT_WITHOUT_ACTION = 32;

export const CSV_HEADERS = [
  "アクション",
  "ファネルID",
  "ファネル名",
  "ランディングページPC用URL必須",
  "ランディングページモバイル用URL",
  "ランディングページタブレット用URL",
  "ランディングページクエリの判定方法",
  "ランディングページURLの一致タイプ",
  "ランディングページフォームの有無",
  "ランディングページホワイトアウトを有効にする",
  "ランディングページシングルページアプリケーション",
  "入力フォームページPC用URL",
  "入力フォームページモバイル用URL",
  "入力フォームページタブレット用URL",
  "入力フォームページクエリの判定方法",
  "入力フォームページURLの一致タイプ",
  "入力フォームページホワイトアウトを有効にする",
  "入力フォームページシングルページアプリケーション",
  "確認ページPC用URL",
  "確認ページ確認ページモバイル用URL",
  "確認ページタブレット用URL",
  "確認ページクエリの判定方法",
  "確認ページURLの一致タイプ",
  "確認ページホワイトアウトを有効にする",
  "確認ページシングルページアプリケーション",
  "サンクスページPC用URL",
  "サンクスページモバイル用URL",
  "サンクスページタブレット用URL",
  "サンクスページクエリの判定方法",
  "サンクスページURLの一致タイプ",
  "サンクスページホワイトアウトを有効にする",
  "サンクスページシングルページアプリケーション",
  "テスト用のファネル",
] as const;

/** Headers when the CSV omits the optional アクション column (matches `CSV_HEADERS.slice(1)`). */
export const FUNNEL_CSV_HEADERS_WITHOUT_ACTION = CSV_HEADERS.slice(1);

export function downloadImportTemplate(): void {
  downloadCsvWithBom("ファネルインポートテンプレート", CSV_HEADERS.join(","));
}

const normalizeHeaderCell = (raw: string | undefined): string =>
  (raw ?? "").replace(/^\uFEFF/, "").trim();

/**
 * Empty/missing → ""; invalid non-empty → null (aligned with popup `parseAction`).
 */
const parseActionCell = (raw: string): "" | "CREATE" | "UPDATE" | null => {
  const t = raw.trim();
  if (!t) return "";
  const upper = t.toUpperCase();
  if (upper === "CREATE" || t === "登録") {
    return "CREATE";
  }
  if (upper === "UPDATE" || t === "編集") {
    return "UPDATE";
  }
  return null;
};

const csvBooleanToBoolean = (value: string): boolean =>
  value === "true" || value === "TRUE" || value === "1";

const emptyToNull = (value: string): string | null =>
  value.trim() === "" ? null : value.trim();

/** CSV column uses same strings as GraphQL `HasForm`. */
const csvToLandingFormIncluded = (raw?: string): boolean => {
  if (raw === undefined) return false;
  const tr = raw.trim();
  return tr === "HAS_FORM";
};

const buildPageValues = (
  url: string,
  mobileUrl: string,
  tabletUrl: string,
  queryMatchType: string,
  urlMatchType: string,
  whiteout: string,
  spa: string,
  landingHasFormColumn?: string
): ImportPageValues => ({
  url: url.trim(),
  mobileUrl: emptyToNull(mobileUrl) ?? "",
  tabletUrl: emptyToNull(tabletUrl) ?? "",
  queryMatchType: queryMatchType.trim() || defaultPageValues.queryMatchType,
  urlMatchType: urlMatchType.trim() || defaultPageValues.urlMatchType,
  ...(landingHasFormColumn !== undefined
    ? {
        landingUrlFormIncluded: csvToLandingFormIncluded(landingHasFormColumn),
      }
    : {}),
  isEnableWhiteout: csvBooleanToBoolean(whiteout),
  isSpa: csvBooleanToBoolean(spa),
});

function colsToFunnelRow(
  cols: string[],
  hasActionColumn: boolean,
  lineNum: number
): { row: FunnelImportRowValues } | { error: string } {
  const expectedLen = hasActionColumn
    ? FUNNEL_IMPORT_COLUMN_COUNT_WITH_ACTION
    : FUNNEL_IMPORT_COLUMN_COUNT_WITHOUT_ACTION;
  if (cols.length !== expectedLen) {
    return {
      error: `行${lineNum}: カラム数が不正です（期待: ${expectedLen}列、実際: ${cols.length}列）`,
    };
  }

  let action: "" | "CREATE" | "UPDATE" = "";
  let landingBase: number;
  let formBase: number;
  let confirmBase: number;
  let thanksBase: number;
  let experimentIdx: number;

  if (hasActionColumn) {
    const parsed = parseActionCell(cols[0]);
    if (parsed === null) {
      return {
        error: `行${lineNum}: アクションは「登録」「編集」または CREATE／UPDATE を指定してください`,
      };
    }
    action = parsed;
    landingBase = 3;
    formBase = 11;
    confirmBase = 18;
    thanksBase = 25;
    experimentIdx = 32;
  } else {
    landingBase = 2;
    formBase = 10;
    confirmBase = 17;
    thanksBase = 24;
    experimentIdx = 31;
  }

  const funnelIdCol = cols[hasActionColumn ? 1 : 0];
  const nameCol = cols[hasActionColumn ? 2 : 1];
  const funnelId = funnelIdCol.trim().replace(/^"|"$/g, "");
  if (action === "") {
    action = funnelId !== "" ? "UPDATE" : "CREATE";
  }

  const row: FunnelImportRowValues = {
    action,
    funnelId,
    name: nameCol.trim(),
    landingPage: buildPageValues(
      cols[landingBase],
      cols[landingBase + 1],
      cols[landingBase + 2],
      cols[landingBase + 3],
      cols[landingBase + 4],
      cols[landingBase + 6],
      cols[landingBase + 7],
      cols[landingBase + 5]
    ),
    formPages:
      cols[formBase].trim() !== ""
        ? [
            buildPageValues(
              cols[formBase],
              cols[formBase + 1],
              cols[formBase + 2],
              cols[formBase + 3],
              cols[formBase + 4],
              cols[formBase + 6],
              cols[formBase + 7],
              cols[formBase + 5]
            ),
          ]
        : [],
    confirmPage: buildPageValues(
      cols[confirmBase],
      cols[confirmBase + 1],
      cols[confirmBase + 2],
      cols[confirmBase + 3],
      cols[confirmBase + 4],
      cols[confirmBase + 6],
      cols[confirmBase + 7],
      cols[confirmBase + 5]
    ),
    thanksPage: buildPageValues(
      cols[thanksBase],
      cols[thanksBase + 1],
      cols[thanksBase + 2],
      cols[thanksBase + 3],
      cols[thanksBase + 4],
      cols[thanksBase + 6],
      cols[thanksBase + 7],
      cols[thanksBase + 5]
    ),
    isExperiment: csvBooleanToBoolean(cols[experimentIdx]),
  };

  return { row };
}

export function parseCsvToFormValues(
  file: File
): Promise<{ rows: FunnelImportRowValues[]; errors: string[] }> {
  return new Promise((resolve) => {
    Papa.parse<string[]>(file, {
      skipEmptyLines: true,
      complete: (result) => {
        const errors: string[] = [];
        const data = result.data;

        if (data.length < 1) {
          resolve({ rows: [], errors: ["CSVファイルが空です"] });
          return;
        }

        const firstRow = data[0];
        const firstCell = normalizeHeaderCell(firstRow[0]);
        const firstLen = firstRow.length;

        let body: string[][];
        let hasActionColumn: boolean;
        let headerRowsSkipped = 0;

        if (
          firstLen === FUNNEL_IMPORT_COLUMN_COUNT_WITH_ACTION &&
          firstCell === "アクション"
        ) {
          body = data.slice(1);
          hasActionColumn = true;
          headerRowsSkipped = 1;
        } else if (
          firstLen === FUNNEL_IMPORT_COLUMN_COUNT_WITHOUT_ACTION &&
          firstCell === CSV_HEADERS[1]
        ) {
          body = data.slice(1);
          hasActionColumn = false;
          headerRowsSkipped = 1;
        } else {
          body = data;
          const nw = body[0]?.length ?? 0;
          if (nw === FUNNEL_IMPORT_COLUMN_COUNT_WITH_ACTION) {
            hasActionColumn = true;
          } else if (nw === FUNNEL_IMPORT_COLUMN_COUNT_WITHOUT_ACTION) {
            hasActionColumn = false;
          } else {
            resolve({
              rows: [],
              errors: [
                `CSVの列数が不正です（アクション列ありで${FUNNEL_IMPORT_COLUMN_COUNT_WITH_ACTION}列、アクション列なしで${FUNNEL_IMPORT_COLUMN_COUNT_WITHOUT_ACTION}列である必要があります。先頭行: ${nw}列）`,
              ],
            });
            return;
          }
        }

        if (body.length === 0) {
          resolve({ rows: [], errors: ["データ行がありません"] });
          return;
        }

        if (body.length > IMPORT_MAX_FUNNELS) {
          resolve({
            rows: [],
            errors: [
              `インポートできるファネル数は${IMPORT_MAX_FUNNELS}件までです（現在: ${body.length}件）`,
            ],
          });
          return;
        }

        const flatRows: FunnelImportRowValues[] = [];
        body.forEach((cols, i) => {
          const lineNum = i + 1 + headerRowsSkipped;
          const parsed = colsToFunnelRow(cols, hasActionColumn, lineNum);
          if ("error" in parsed) {
            errors.push(parsed.error);
            return;
          }
          flatRows.push(parsed.row);
        });

        // Group consecutive UPDATE rows with the same funnelId, or consecutive
        // CREATE rows with the same funnel name, into one funnel with merged form pages.
        const rows: FunnelImportRowValues[] = [];
        flatRows.forEach((row) => {
          if (
            row.action === "UPDATE" &&
            row.funnelId !== "" &&
            rows.length > 0
          ) {
            const last = rows[rows.length - 1];
            if (last.action === "UPDATE" && last.funnelId === row.funnelId) {
              last.formPages = [...last.formPages, ...row.formPages];
              return;
            }
          }
          if (
            row.action === "CREATE" &&
            row.name.trim() !== "" &&
            rows.length > 0
          ) {
            const last = rows[rows.length - 1];
            if (
              last.action === "CREATE" &&
              last.name.trim() !== "" &&
              last.name.trim() === row.name.trim()
            ) {
              last.formPages = [...last.formPages, ...row.formPages];
              return;
            }
          }
          rows.push({ ...row });
        });

        resolve({ rows, errors });
      },
      error: () => {
        resolve({ rows: [], errors: ["CSVファイルの読み込みに失敗しました"] });
      },
    });
  });
}
